import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';

function normalizeValues(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter(Number.isFinite)
    .slice(-24);
}

export function MiniChart({ values, label, compact = false, unit = '/min' }) {
  const ref = useRef(null);
  const series = useMemo(() => normalizeValues(values), [values]);
  const current = series.at(-1) ?? 0;
  const peak = series.length ? Math.max(...series) : 0;
  const active = peak > 0;

  useEffect(() => {
    if (!ref.current) return undefined;

    const chart = echarts.init(ref.current, undefined, { renderer: 'canvas' });
    const axisMax = Math.max(4, Math.ceil(peak * 1.25));
    const categories = series.map((_, index) => index);

    chart.setOption({
      animationDuration: 420,
      animationEasing: 'cubicOut',
      backgroundColor: 'transparent',
      grid: compact
        ? { left: 28, right: 8, top: 34, bottom: 18, containLabel: false }
        : { left: 36, right: 12, top: 42, bottom: 24, containLabel: false },
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        backgroundColor: 'rgba(3, 8, 15, .96)',
        borderColor: 'rgba(103, 232, 249, .34)',
        borderWidth: 1,
        padding: [7, 9],
        textStyle: { color: '#d8fafe', fontSize: 10, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
        axisPointer: { type: 'line', lineStyle: { color: 'rgba(103,232,249,.34)', width: 1 } },
        formatter: (params) => `${label}<br/><b>${params?.[0]?.value ?? 0}</b> ${unit}`,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisLine: { show: true, lineStyle: { color: 'rgba(148,163,184,.10)' } },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: 'rgba(100,116,139,.72)',
          fontSize: 8,
          interval: 0,
          formatter: (_value, index) => {
            if (index === 0) return '-5m';
            if (index === categories.length - 1) return 'now';
            return '';
          },
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: 'rgba(100,116,139,.72)', fontSize: 8, margin: 6 },
        splitLine: { show: true, lineStyle: { color: 'rgba(148,163,184,.07)', type: 'dashed' } },
      },
      series: [{
        name: label,
        type: 'line',
        data: series,
        smooth: 0.32,
        connectNulls: true,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          width: 1.7,
          color: '#67e8f9',
          shadowBlur: 9,
          shadowColor: 'rgba(103,232,249,.28)',
        },
        areaStyle: {
          opacity: 1,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(103,232,249,.22)' },
            { offset: 0.55, color: 'rgba(103,232,249,.06)' },
            { offset: 1, color: 'rgba(103,232,249,0)' },
          ]),
        },
        markPoint: active ? {
          symbol: 'circle',
          symbolSize: 6,
          label: { show: false },
          itemStyle: { color: '#a5f3fc', borderColor: '#041018', borderWidth: 2 },
          data: [{ coord: [categories.at(-1), current] }],
        } : undefined,
      }],
    });

    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
      chart.dispose();
    };
  }, [compact, current, label, peak, series, unit, active]);

  return <div className={`telemetry-chart ${compact ? 'telemetry-chart-compact' : ''}`}>
    <div className="telemetry-chart-meta">
      <div>
        <p>{label}</p>
        <span>{active ? 'Realtime throughput' : 'Waiting for rider telemetry'}</span>
      </div>
      <div className="telemetry-chart-value">
        <strong>{current}</strong>
        <span>{unit}</span>
      </div>
    </div>
    <div ref={ref} className="telemetry-chart-canvas" />
    {!active && <div className="telemetry-chart-idle"><span /> IDLE</div>}
  </div>;
}
